import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, Check, CheckCircle2, Download, ExternalLink, FileText, Loader2, Stamp, Wallet, XCircle } from "lucide-react";

import { ActionButton, IconTile, Modal, Notice, StatusPill, cx } from "../../customer/account/account.ui";
import { formatDate, formatMoney, titleCaseName } from "../../customer/account/filing/filing.helpers";
import RequestFilingModal from "./request.filing.modal";
import FilingBalanceModal from "./filing.balance.modal";
import FilingManualForm from "./filing.manual.form";
import { filingVerbs, isActiveFiling } from "./filing.labels";
import { failedNoticeOf, filingLockOf, formatAt, receiptLabelOf, receiptsOf, scheduledStartOf, signerNamesOf } from "./filing.rules";

import filingService from "../../../domain/services/filing/filing.service";
import loginService from "../../../domain/services/login.service";

const formatDateTime = (value) => (value
    ? new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '');

const timelineSteps = (verbs) => [
    { status: 'queued', label: 'Pedido recebido' },
    { status: 'organizing', label: 'Preparando os documentos' },
    { status: 'filing', label: 'Enviando ao tribunal' },
    { status: 'filed', label: verbs.done },
];

const STEP_ORDER = ['queued', 'organizing', 'filing', 'filed'];

export function Timeline({ filing, verbs }) {
    const reached = filing.status === 'needs_customer'
        ? STEP_ORDER.indexOf((filing.history || []).map(entry => entry.status).filter(status => STEP_ORDER.includes(status)).pop() || 'queued')
        : STEP_ORDER.indexOf(filing.status);
    const atOf = (status) => [...(filing.history || [])].reverse().find(entry => entry.status === status)?.at;

    return (
        <ol className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-4 sm:gap-2">
            {timelineSteps(verbs).map((step, index) => {
                const isDone = index < reached || (index === reached && filing.status === 'filed');
                const isCurrent = index === reached && filing.status !== 'filed';
                const isBlocked = isCurrent && filing.status === 'needs_customer';

                return (
                    <li key={step.status} className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-2">
                        <div className="flex items-center gap-2 sm:w-full">
                            <span className={cx(
                                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                                isDone && 'bg-primary text-white',
                                isCurrent && !isBlocked && 'bg-primaryTint text-primary ring-2 ring-primary',
                                isBlocked && 'bg-[#fdf1e3] text-[#9a5512] ring-2 ring-[#e9a34a]',
                                !isDone && !isCurrent && 'bg-[#f2f2f5] text-[#8a8a94]',
                            )}>
                                {isDone ? <Check className="h-3.5 w-3.5" aria-hidden="true" />
                                    : isBlocked ? '!'
                                        // Etapa em curso: a gente está trabalhando, não é um passo para o cliente fazer.
                                        : isCurrent ? <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-label="Em andamento" />
                                            : index + 1}
                            </span>
                            {index < STEP_ORDER.length - 1 && <span className={cx('hidden h-0.5 flex-1 rounded sm:block', index < reached ? 'bg-primary' : 'bg-[#ececf0]')} />}
                        </div>
                        <div>
                            <p className={cx('m-0 text-[13px] font-medium', isDone || isCurrent ? 'text-[#1d1d1f]' : 'text-[#8a8a94]')}>
                                {isBlocked ? 'Precisa de você' : step.label}
                            </p>
                            {(isDone || isCurrent) && atOf(step.status) && <p className="m-0 text-xs text-[#8a8a94]">{formatDateTime(atOf(step.status))}</p>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

function FilingTracker({ panel, onCancel }) {
    const { filing } = panel;
    const verbs = filingVerbs(panel.type);
    // O robô segue tentando sozinho; o pedido não falhou.
    const courtDown = filing.reasonCode === 'COURT_UNAVAILABLE' && filing.status !== 'filed' && filing.status !== 'needs_customer';
    const courtDocumentId = filing.courtDocumentId || filing.receipt?.courtDocumentId;
    // Link do robô: só https vira link na tela.
    const actUrl = [filing.actUrl, filing.receipt?.actUrl].find((url) => /^https:\/\//i.test(String(url || '')));
    // Pedido fora do horário: o robô só começa no próximo dia útil.
    const startsAt = filing.status === 'queued' ? scheduledStartOf(filing) : null;
    const signerNames = signerNamesOf(filing).map(titleCaseName);
    const requestedAt = formatAt(filing.requestedAt);
    const courtLine = `${panel.court?.name || 'Tribunal'}${requestedAt ? ` · Pedido em ${requestedAt}` : ''}.`;
    const receipts = filing.status === 'filed' ? receiptsOf(filing) : [];
    // O que foi (ou vai) ao tribunal, na ordem do envio. Mídia não vai no protocolo automático.
    const sentDocuments = (Array.isArray(filing.documents) ? filing.documents : [])
        .filter(document => document?.name && !document.media)
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    // F399: a peça vai com o nome do tipo ("Petição inicial.pdf"); o item 1 diz se é a versão do cliente (com o nome do
    // arquivo dele) ou a entregue pela Facilita (Natã, 432653, 03/11/2026).
    const pieceSource = filing.documentSource === 'customer_final'
        ? `sua versão${filing.documentName ? `: ${filing.documentName}` : ''}`
        : filing.documentSource ? 'versão da Facilita' : null;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <IconTile icon={Stamp} tone={filing.status === 'filed' ? 'success' : filing.status === 'needs_customer' ? 'warning' : 'brand'} />
                    <div>
                        <p className="m-0 flex flex-wrap items-center gap-2 text-[15px] font-semibold text-[#1d1d1f]">
                            {`${verbs.noun[0].toUpperCase()}${verbs.noun.slice(1)} pela Facilita`}
                            {filing.status === 'filed' ? <StatusPill tone="success">{`${verbs.done} em ${formatAt(filing.filedAt) || formatDate(filing.filedAt)}`}</StatusPill>
                                : filing.status === 'needs_customer' ? <StatusPill tone="warning">Precisa de você</StatusPill>
                                    : courtDown ? <StatusPill tone="warning">Aguardando o tribunal</StatusPill>
                                        : <StatusPill>Em andamento</StatusPill>}
                        </p>
                        <p className="m-0 mt-0.5 text-[13px] text-[#6b6b76]">
                            {filing.status === 'filed'
                                ? (filing.protocolNumber ? `${verbs.number} ${filing.protocolNumber}` : 'Comprovante enviado por e-mail')
                                : filing.status === 'needs_customer'
                                    ? panel.court?.name || 'Tribunal'
                                    : courtDown
                                        ? `O ${panel.court?.name?.split(' · ')[0] || 'tribunal'} está fora do ar. Assim que voltar, ${verbs.we} sozinhos e te avisamos. Você não precisa fazer nada.`
                                        : startsAt
                                            ? `${courtLine} ${verbs.We} no próximo dia útil, a partir das 8h.`
                                            : `${courtLine} Pedidos feitos até as 20h são ${verbs.donePlural} até o fim do dia. Avisamos por e-mail e aqui a cada etapa.`}
                        </p>
                    </div>
                </div>
                {receipts.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="filing-receipts">
                        {receipts.map((receipt) => (
                            <a key={receipt.id || receipt.url} href={receipt.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-[#e6e6ea] bg-white px-3 py-1.5 text-sm font-medium text-[#1d1d1f] hover:bg-[#fafafb]">
                                <Download className="h-4 w-4" aria-hidden="true" />
                                {receiptLabelOf(receipt)}
                            </a>
                        ))}
                    </div>
                )}
                {/* A API decide (mesma regra da rota de cancelamento); sem o campo, não oferece. */}
                {panel.canCancel === true && (
                    <ActionButton size="sm" variant="ghost" onClick={onCancel}>Cancelar pedido</ActionButton>
                )}
                {panel.canCancel !== true && panel.cancelBlockedReason === 'CANCELAMENTO_INDISPONIVEL' && (
                    <p className="m-0 text-[13px] text-[#6b6b76]">O envio já começou: não dá mais para cancelar.</p>
                )}
            </div>

            <Timeline filing={filing} verbs={verbs} />

            {signerNames.length > 0 && (
                <p className="m-0 text-[13px] text-[#6b6b76]" data-testid="filing-signers">
                    {signerNames.length === 1 ? 'Assina: ' : 'Assinam: '}
                    <b className="font-medium text-[#1d1d1f]">{signerNames[0]}</b>
                    {signerNames.length > 1 && ' (principal)'}
                    {signerNames.slice(1).map((name, index) => (
                        <span key={name}>
                            {index === signerNames.length - 2 ? ' e ' : ', '}
                            <b className="font-medium text-[#1d1d1f]">{name}</b>
                        </span>
                    ))}
                </p>
            )}

            {sentDocuments.length > 0 && (
                <div className="rounded-lg border border-[#ececf0] bg-[#fafafb] px-4 py-3" data-testid="filing-sent-documents">
                    <p className="m-0 text-[13px] font-semibold text-[#1d1d1f]">
                        {filing.status === 'filed' ? 'O que foi ao tribunal' : 'O que vai ao tribunal'}
                        <span className="font-normal text-[#6b6b76]">{` · ${sentDocuments.length} ${sentDocuments.length === 1 ? 'documento' : 'documentos'}`}</span>
                    </p>
                    <ol className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
                        {sentDocuments.map((document, index) => (
                            <li key={document.id || index} className="flex items-center gap-2 text-[13px] text-[#3a3a44]">
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-[#6b6b76] ring-1 ring-[#e6e6ea]">{index + 1}</span>
                                <FileText className="h-3.5 w-3.5 shrink-0 text-[#8a8a94]" aria-hidden="true" />
                                <span className="min-w-0 truncate">{document.name}</span>
                                {index === 0 && <span className="min-w-0 truncate text-xs text-[#8a8a94]" data-testid="filing-piece-source">{pieceSource ? `peça principal · ${pieceSource}` : 'peça principal'}</span>}
                                {document.secret && <span className="shrink-0 text-xs text-[#8a8a94]">sigiloso</span>}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {filing.status === 'filed' && (courtDocumentId || actUrl) && (
                <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[#6b6b76]">
                    {courtDocumentId && <span>ID do documento no tribunal: <b className="font-medium text-[#1d1d1f]">{courtDocumentId}</b></span>}
                    {actUrl && (
                        <a href={actUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                            Ver o ato nos autos <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                    )}
                </p>
            )}

            {filing.status === 'needs_customer' && (
                <Notice tone="warning" icon={AlertTriangle} title="Precisamos de você para seguir">
                    {filing.reason || 'Nossa equipe enviou os detalhes por e-mail.'} {NEXT_STEP[filing.reasonCode] || NEXT_STEP.default}
                    {filing.reasonCode === 'ACCESS_ACTIVATION' && <a href="/codes" className="mt-2 block text-sm font-semibold text-primary hover:underline">Ativar código do PJe</a>}
                </Notice>
            )}
        </div>
    );
}

// O que devolve o pedido à fila: documento anexado e certificado renovado voltam sozinhos; o resto, pelo comentário.
export const NEXT_STEP = {
    DOCUMENTS: 'Anexe o documento na aba Documentos desta solicitação: o pedido volta para a fila sozinho.',
    CERTIFICATE: 'Atualize o certificado digital em Minha conta → Certificado e tribunais: o pedido volta para a fila sozinho.',
    ACCESS_ACTIVATION: 'Ative o código em Minha conta → Códigos dos tribunais. Quando ficar ativo, este pedido volta para a fila.',
    ACCESS_RECONNECT: 'Reconecte o acesso ao tribunal em Minha conta → Certificado e tribunais: o pedido volta para a fila sozinho.',
    default: 'Quando resolver, responda na aba Comentários desta solicitação e seguimos daqui.',
};

// Faixa do registro manual: "Protocolada por você em X" ou o convite para registrar a data.
function ManualFilingStrip({ record, verbs, onOpen }) {
    const at = formatDate(record?.at);
    const doneByYou = `${verbs.doneFem[0].toUpperCase()}${verbs.doneFem.slice(1)} por você`;

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#ececf0] bg-white px-5 py-4" data-testid="filing-manual-strip">
            <div className="flex items-center gap-3">
                <IconTile icon={record ? CheckCircle2 : Stamp} tone={record ? 'success' : 'neutral'} />
                <div>
                    <p className="m-0 text-[15px] font-semibold text-[#1d1d1f]">
                        {record ? `${doneByYou}${at ? ` em ${at}` : ''}` : `Já ${verbs.imperative === 'distribua' ? 'distribuiu' : 'protocolou'}?`}
                    </p>
                    <p className="m-0 mt-0.5 text-[13px] text-[#6b6b76]">
                        {record ? 'Registrado por você. Nada foi enviado pela Facilita.' : 'Registre a data para controlar seus prazos.'}
                    </p>
                </div>
            </div>
            <ActionButton variant="secondary" onClick={onOpen}>{record ? 'Alterar' : 'Registrar data'}</ActionButton>
        </div>
    );
}

export default function RequestFilingPanel({ request, setAlert, refresh }) {
    const requestId = request.id;

    const location = useLocation();
    const [panel, setPanel] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isManualOpen, setIsManualOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [balance, setBalance] = useState(null);
    const [isCancelOpen, setIsCancelOpen] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    // Falha ao recarregar mantém o painel que já estava na tela (só o primeiro carregamento sem resposta esconde tudo).
    const load = () => filingService
        .findRequestFiling(requestId)
        .then((result) => { setPanel(result); setLoadFailed(false); })
        .catch(() => setLoadFailed(true));

    useEffect(() => { load(); }, [requestId, request.status]);

    // Registro manual (data do protocolo feito pelo cliente): some só com pedido nosso valendo (a API diz; API antiga:
    // pelo status). Fica neste painel, no lugar da antiga linha "Serviço protocolado em".
    const manualAllowed = !panel || (typeof panel.manualFilingAllowed === 'boolean'
        ? panel.manualFilingAllowed
        : !isActiveFiling(panel.filing));

    // Link direto (?protocolar=1, ex.: botão do e-mail de entrega) abre a conferência assim que o painel carrega.
    const canRequest = !!panel?.available && !isActiveFiling(panel?.filing) && !filingLockOf(panel, filingVerbs(panel?.type));
    // Abre uma vez só: depois de cancelar ou de um pedido que falhou, o parâmetro continua na URL.
    const autoOpened = useRef(false);
    useEffect(() => {
        if (canRequest && !autoOpened.current && new URLSearchParams(location.search).get('protocolar') === '1') {
            autoOpened.current = true;
            setIsModalOpen(true);
        }
    }, [canRequest]);
    // "Já protocolei" no card da lista (?registrar=1) abre o registro da data aqui.
    const manualOpened = useRef(false);
    useEffect(() => {
        if ((panel || loadFailed) && manualAllowed && !manualOpened.current && new URLSearchParams(location.search).get('registrar') === '1') {
            manualOpened.current = true;
            setIsManualOpen(true);
        }
    }, [panel, loadFailed, manualAllowed]);

    if (!panel && !loadFailed)
        return null;

    const verbs = filingVerbs(panel?.type);
    const filing = panel?.filing;

    const manualSaved = (message) => {
        setIsManualOpen(false);
        setAlert?.(message, 'success');
        load();
        refresh?.();
    };

    if (isManualOpen && manualAllowed)
        return (
            <div className="rounded-xl border border-[#ececf0] bg-white px-5 py-4">
                <FilingManualForm request={request} verbs={verbs} setAlert={setAlert} onSaved={manualSaved} onCancel={() => setIsManualOpen(false)} />
            </div>
        );

    // Data marcada pelo cliente: a da API (alreadyFiled manual) ou, sem painel, a da solicitação.
    const manualRecord = panel?.alreadyFiled?.source === 'manual' ? panel.alreadyFiled
        : request.protocoledAt ? { at: request.protocoledAt } : null;
    const manualStrip = <ManualFilingStrip record={manualRecord} verbs={verbs} onOpen={() => setIsManualOpen(true)} />;

    // Sem painel (falha ao carregar) ou com a data marcada pelo cliente: a faixa do registro manual.
    if (!panel || (manualAllowed && panel.alreadyFiled?.source === 'manual'))
        return manualStrip;

    const cancel = async () => {
        setIsCancelling(true);
        try {
            await filingService.cancelFiling(requestId);
            setAlert?.(Number(filing?.fee) > 0 ? `Pedido cancelado. Devolvemos ${formatMoney(filing.fee)} ao seu saldo.` : 'Pedido cancelado.', 'success');
            setIsCancelOpen(false);
            load();
        } catch (failure) {
            setAlert?.(filingService.errorMessageOf(failure), 'error');
            // A equipe pode já ter começado: fecha e mostra a etapa atual.
            setIsCancelOpen(false);
            load();
        } finally {
            setIsCancelling(false);
        }
    };

    const filed = (result) => {
        setIsModalOpen(false);
        setAlert?.('Pedido recebido. Avisamos a cada etapa.', 'success');
        load();
        refresh?.();
    };

    if (isActiveFiling(filing))
        return (
            <div className="rounded-xl border border-[#ececf0] bg-white px-5 py-4">
                <FilingTracker panel={panel} onCancel={() => setIsCancelOpen(true)} />
                <Modal
                    open={isCancelOpen}
                    onClose={() => setIsCancelOpen(false)}
                    icon={XCircle}
                    size="sm"
                    title="Cancelar o pedido?"
                    description={Number(filing.fee) > 0
                        ? `${verbs.theNoun[0].toUpperCase()}${verbs.theNoun.slice(1)} ainda não começou. Devolvemos ${formatMoney(filing.fee)} ao seu saldo.`
                        : `${verbs.theNoun[0].toUpperCase()}${verbs.theNoun.slice(1)} ainda não começou. Você pode pedir de novo quando quiser.`}
                    footer={(
                        <>
                            <ActionButton variant="secondary" onClick={() => setIsCancelOpen(false)} disabled={isCancelling}>Manter</ActionButton>
                            <ActionButton variant="danger" loading={isCancelling} onClick={cancel}>Cancelar pedido</ActionButton>
                        </>
                    )}
                />
            </div>
        );

    // Tribunal ou cliente sem protocolo pela Facilita: só o registro da data.
    if (!panel.available)
        return manualAllowed ? manualStrip : null;

    const lock = filingLockOf(panel, verbs);

    // Já protocolada (ou outro pedido em andamento): o cartão vira o aviso, sem oferta nem preço.
    if (lock)
        return (
            <div className="flex items-start gap-3 rounded-xl border border-[#ececf0] bg-white px-5 py-4" data-testid="filing-lock">
                <IconTile icon={panel.alreadyFiled ? CheckCircle2 : Loader2} tone={panel.alreadyFiled ? 'success' : 'neutral'} />
                <div>
                    <p className="m-0 text-[15px] font-semibold text-[#1d1d1f]">
                        {panel.alreadyFiled ? `Peça já ${verbs.doneFem}` : `${verbs.noun[0].toUpperCase()}${verbs.noun.slice(1)} em andamento`}
                    </p>
                    <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-3 text-[13px] text-[#6b6b76]">
                        {lock.text}
                        {lock.receiptUrl && (
                            <a href={lock.receiptUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                                <Download className="h-3.5 w-3.5" aria-hidden="true" /> Comprovante
                            </a>
                        )}
                    </p>
                </div>
            </div>
        );

    // Pedido anterior encerrado sem protocolar: a mensagem do motivo. Sem o que pedir de novo, o cartão vira o aviso.
    const failedNotice = failedNoticeOf(filing, verbs, panel.court?.name);
    if (failedNotice && !failedNotice.retry)
        return (
            <div className="flex items-start gap-3 rounded-xl border border-[#ececf0] bg-white px-5 py-4" data-testid="filing-failed">
                <IconTile icon={AlertTriangle} tone="warning" />
                <div>
                    <p className="m-0 text-[15px] font-semibold text-[#1d1d1f]">{failedNotice.title}</p>
                    <p className="m-0 mt-0.5 text-[13px] text-[#6b6b76]">{failedNotice.text}</p>
                </div>
            </div>
        );

    const noBalance = Number(panel.fee) > 0 && panel.balance?.enough === false;
    const canBuy = loginService.isCustomerAdmin();
    const courtShort = panel.court?.name?.split(' · ')[0] || '';
    // Guia anexada e sem comprovante de pagamento: o comprovante entra pela aba Documentos e vai junto.
    const requirementsOf = (type) => (panel.requirements || []).filter(item => item.documentType === type);
    const asksFeeReceipt = requirementsOf('fee_slip').some(item => item.documents?.length)
        && !requirementsOf('fee_receipt').some(item => item.documents?.length);

    return (
        <div className="flex flex-col gap-4 rounded-xl border border-[#ececf0] bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
                <IconTile icon={Stamp} />
                <div>
                    <p className="m-0 flex flex-wrap items-center gap-2 text-[15px] font-semibold text-[#1d1d1f]" data-testid="filing-ready-title">
                        {`Pronta para ${verbs.action.toLowerCase()}${courtShort ? ` no ${courtShort}` : ''}`}
                    </p>
                    <p className="m-0 mt-0.5 text-[13px] text-[#6b6b76]">
                        Organizamos os anexos no padrão do {courtShort || 'tribunal'} e {verbs.we} {panel.court?.system === 'meu_inss' ? 'com o acesso gov.br do segurado' : 'com o seu certificado'}. Você acompanha aqui e recebe o comprovante por e-mail.
                        {Number(panel.fee) > 0 ? ` ${formatMoney(panel.fee)} por ${verbs.noun}, debitado do seu pacote.` : ' Grátis no seu plano.'}
                    </p>
                    {asksFeeReceipt && (
                        <p className="m-0 mt-1 text-[13px] text-[#3a3a44]" data-testid="filing-fee-receipt-hint">
                            Pagou a guia? Anexe o comprovante de pagamento na aba Documentos: ele vai junto {verbs.noun === 'distribuição' ? 'na distribuição' : 'no protocolo'}.
                        </p>
                    )}
                    {noBalance && (
                        <p className="m-0 mt-1 text-[13px] font-medium text-[#9a5512]" data-testid="filing-no-balance">
                            Seu pacote tem {formatMoney(panel.balance.remaining)}, menos que a taxa.{' '}
                            {canBuy ? `Renove para ${verbs.imperative === 'distribua' ? 'distribuir' : 'protocolar'} esta peça.` : 'Peça ao responsável pela conta para renovar o pacote.'}
                        </p>
                    )}
                    {failedNotice && (
                        <p className="m-0 mt-1 text-[13px] text-[#9a5512]" data-testid="filing-failed">
                            <b className="font-semibold">{failedNotice.title}.</b> {failedNotice.text}
                        </p>
                    )}
                    {manualAllowed && (
                        <button type="button" className="mt-2 p-0 text-[13px] text-[#55555f] hover:text-primary hover:underline" onClick={() => setIsManualOpen(true)}>
                            {`Já ${verbs.imperative === 'distribua' ? 'distribuiu' : 'protocolou'} por conta própria? Registre a data`}
                        </button>
                    )}
                </div>
            </div>
            {noBalance
                ? canBuy && <ActionButton icon={Wallet} onClick={() => setBalance({ fee: panel.fee, remaining: panel.balance.remaining })} className="shrink-0">Renovar pacote</ActionButton>
                : <ActionButton icon={Stamp} onClick={() => setIsModalOpen(true)} className="shrink-0">{verbs.action}</ActionButton>}

            <RequestFilingModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                requestId={requestId}
                panel={panel}
                onReload={load}
                onFiled={filed}
                onNoBalance={(info) => { setIsModalOpen(false); setBalance(info); }}
            />

            <FilingBalanceModal
                open={!!balance}
                onClose={() => setBalance(null)}
                fee={balance?.fee}
                remaining={balance?.remaining}
                verbs={verbs}
            />
        </div>
    );
}
